import type {Lambda} from "mobx";
import * as React from "react";
import styled from "styled-components";
import {observer} from "mobx-react";
import {reaction} from "mobx";
import type {SpaceViewRenderer} from "../../../modules/space/spaceeditor/logic3d/renderers/SpaceViewRenderer";
import type {SelectionToolType, SpaceTool} from "../../../modules/space/spaceeditor/logic3d/features/tools/Tools";
import type {IIconConfig} from "../../../modules/catalog/create/CatalogTypes";
import type {Catalog} from "../../../../data/models/Catalog";
import type {ISpaceItemModel} from "../../../../data/models/Model";
import type {RescaleSpaceRequest, SpaceFileDto, SpaceFileInsertionInfo, UpdateSpaceFileRequest} from "../../../../generated/api/base";
import type {DockableTitle} from "../../../modules/space/spaceeditor/ui/toolbar/Dockable";
import type {IFilterState} from "../../../../data/models/filter/Filter";
import {arrayOfSelectionToolTypes} from "../../../modules/space/spaceeditor/logic3d/features/tools/Tools";
import {XyiconFeature} from "../../../../generated/api/base";
import {XHRLoader} from "../../../../utils/loader/XHRLoader";
import {Functions} from "../../../../utils/function/Functions";
import {SpaceEditorMode} from "../../../modules/space/spaceeditor/logic3d/renderers/SpaceViewRendererUtils";
import {VerticalFlexStyle} from "../../styles/styles";
import {DetailsContentV5} from "../../details/DetailsContentV5";
import {Panel} from "../../abstract/Panel";
import {KeyboardListener} from "../../../../utils/interaction/key/KeyboardListener";
import {SideGrid} from "../../abstract/SideGrid";
import {SplitterV5} from "../../layout/SplitterV5";
import {
	SPLITTER_V5_LOCAL_STORAGE_KEY_SPACE_EDITOR_DOCK_MAIN,
	ensureSpaceEditorDockMainSeededFromLegacyCatalogKey,
} from "../../layout/splitterLayoutMath";
import type {BoundarySpaceMap} from "../../../../data/models/BoundarySpaceMap";
import {CatalogCreateV5} from "../catalog/CatalogCreateV5";
import {StringUtils} from "../../../../utils/data/string/StringUtils";
import {ArrayUtils} from "../../../../utils/data/array/ArrayUtils";
import type {TSpaceEditorPanelTypes} from "./SpaceEditorPanelV5";
import {SpaceEditorPanelV5} from "./SpaceEditorPanelV5";
import {DockableV5} from "./dockable/DockableV5";
import {SpaceEditorV5} from "./SpaceEditorV5";
import type {GridItem, GridSide} from "./spaceselector/GridTypes";

interface IBasicPropsFor3DTweaking {
	readonly spaceViewRenderer: SpaceViewRenderer;
}

interface ISpaceViewState {
	focusedItems: ISpaceItemModel[];
	isCatalogOpen: boolean;
	isCatalogDocked: boolean;
	isBoundaryTypesWindowOpen: boolean;
	isBoundaryTypesWindowDocked: boolean;
	isUnplottedXyiconsOpen: boolean;
	isUnplottedXyiconsDocked: boolean;
	activeToolId: SpaceTool;
	selectionToolType: SelectionToolType;
	isCreatePanelOpen: boolean;
	editedCatalog: {
		config: IIconConfig;
		catalog: Catalog;
	};
	openedSpaceEditorPanelType: TSpaceEditorPanelTypes | null;
}
@observer
export class SpaceEditorViewV5 extends React.Component<IBasicPropsFor3DTweaking, ISpaceViewState> {
	private _spaceViewRenderer: SpaceViewRenderer;

	private _requestAnimationFrameId: number = null;
	private _disposerForViewChangeListener: Lambda;
	private _disposerForFilterListener: Lambda;
	private readonly _updateFilterStatePromiseResolveSet: Set<(value: void | PromiseLike<void>) => void> = new Set();
	private _closeActionBarPopups: (() => void) | null = null;

	constructor(props: IBasicPropsFor3DTweaking) {
		super(props);
		this._spaceViewRenderer = props.spaceViewRenderer;

		this.state = {
			focusedItems: [],
			isCatalogOpen: false,
			isCatalogDocked: false,
			isBoundaryTypesWindowOpen: false,
			isBoundaryTypesWindowDocked: false,
			isUnplottedXyiconsOpen: false,
			isUnplottedXyiconsDocked: false,
			activeToolId: "selection",
			selectionToolType: "selection",
			isCreatePanelOpen: false,
			editedCatalog: null,
			openedSpaceEditorPanelType: null,
		};
	}

	private triggerSpaceResize = () => {
		const {spaceViewRenderer} = this.props;

		if (!spaceViewRenderer?.isMounted) {
			return;
		}

		// Similar workaround as in PhotoSphereViewer: call onResize twice on consecutive frames
		// to avoid occasional stretched / incorrectly sized canvas after layout changes.
		requestAnimationFrame(() => {
			if (!spaceViewRenderer.isMounted) {
				return;
			}

			spaceViewRenderer.onResize();

			requestAnimationFrame(() => {
				if (spaceViewRenderer.isMounted) {
					spaceViewRenderer.onResize();
				}
			});
		});
	};

	private setDockableDocked = (value: boolean, title: DockableTitle) => {
		this.closeCreatePanel();
		this.props.spaceViewRenderer.transport.appState.catalogEditorParentMaybe = null;

		if (title === "Catalog") {
			this.setState({
				isCatalogDocked: value,
			});

			if (value === this.state.isBoundaryTypesWindowDocked) {
				this.setState({
					isBoundaryTypesWindowOpen: false,
				});
			} else if (value === this.state.isUnplottedXyiconsDocked) {
				this.setState({
					isUnplottedXyiconsOpen: false,
				});
			}
		} else if (title === "Boundary") {
			this.setState({
				isBoundaryTypesWindowDocked: value,
			});

			if (value === this.state.isCatalogDocked) {
				this.setState({
					isCatalogOpen: false,
				});
			} else if (value === this.state.isUnplottedXyiconsDocked) {
				this.setState({
					isUnplottedXyiconsOpen: false,
				});
			}
		} else if (title === "Unplotted Xyicons") {
			this.setState({
				isUnplottedXyiconsDocked: value,
			});

			if (value === this.state.isCatalogDocked) {
				this.setState({
					isCatalogOpen: false,
				});
			}
			if (value === this.state.isBoundaryTypesWindowDocked) {
				this.setState({
					isBoundaryTypesWindowOpen: false,
				});
			}
		}
	};

	private isDocked(title: DockableTitle) {
		if (title === "Catalog") {
			return this.state.isCatalogDocked;
		} else if (title === "Boundary") {
			return this.state.isBoundaryTypesWindowDocked;
		} else if (title === "Unplotted Xyicons") {
			return this.state.isUnplottedXyiconsDocked;
		}
	}

	private onCatalogPanelClosed = () => {
		this.closeCreatePanel();
		this.props.spaceViewRenderer.transport.appState.catalogEditorParentMaybe = null;
	};

	private setDockableOpen = (value: boolean, title: DockableTitle, onlyIfNotDocked: boolean = false) => {
		const isDocked = this.isDocked(title);

		if (value) {
			this._spaceViewRenderer.markupManager.removeAllTempMarkups();
			this._spaceViewRenderer.isMeasureToolBarOpen = false;
			this.setActiveTool("selection");
		}

		if ((onlyIfNotDocked && !isDocked) || !onlyIfNotDocked) {
			if (title === "Catalog") {
				if (!value) {
					this.onCatalogPanelClosed();
				}
				this.setState({
					isCatalogOpen: value,
				});

				if (value && this.state.isBoundaryTypesWindowOpen && this.state.isBoundaryTypesWindowDocked === this.state.isCatalogDocked) {
					this.setState({
						isBoundaryTypesWindowOpen: false,
					});
				}
				if (value && this.state.isUnplottedXyiconsOpen && this.state.isUnplottedXyiconsDocked === this.state.isCatalogDocked) {
					this.setState({
						isUnplottedXyiconsOpen: false,
					});
				}
			} else if (title === "Boundary") {
				this.setState({
					isBoundaryTypesWindowOpen: value,
				});

				if (value && this.state.isCatalogOpen && this.state.isCatalogDocked === this.state.isBoundaryTypesWindowDocked) {
					this.onCatalogPanelClosed();
					this.setState({
						isCatalogOpen: false,
					});
				}
				if (value && this.state.isUnplottedXyiconsOpen && this.state.isUnplottedXyiconsDocked === this.state.isBoundaryTypesWindowDocked) {
					this.setState({
						isUnplottedXyiconsOpen: false,
					});
				}
			} else if (title === "Unplotted Xyicons") {
				this.setState({
					isUnplottedXyiconsOpen: value,
				});

				if (value && this.state.isCatalogOpen && this.state.isCatalogDocked === this.state.isUnplottedXyiconsDocked) {
					this.onCatalogPanelClosed();
					this.setState({
						isCatalogOpen: false,
					});
				}
				if (value && this.state.isBoundaryTypesWindowOpen && this.state.isBoundaryTypesWindowDocked === this.state.isUnplottedXyiconsDocked) {
					this.setState({
						isBoundaryTypesWindowOpen: false,
					});
				}
			}
		}
	};

	private setScale = async (spaceUnitsPerMeter: number) => {
		const {transport} = this.props.spaceViewRenderer;
		const {appState} = transport;
		const {space} = appState;
		const params: RescaleSpaceRequest = {
			spaceID: space.id,
			unitsPerMeter: spaceUnitsPerMeter,
			portfolioID: appState.activePortfolioId,
		};

		await transport.requestForOrganization<SpaceFileDto>({
			url: "spaces/rescale",
			method: XHRLoader.METHOD_POST,
			params: params,
		});

		space.setSpaceUnitsPerMeter(spaceUnitsPerMeter);

		await this.props.spaceViewRenderer.refreshSpace(true);
	};

	private confirmAlignment = async (insertionInfo: SpaceFileInsertionInfo) => {
		const activePDFFile = this.props.spaceViewRenderer.space.selectedSpaceFile;

		activePDFFile.setInsertionInfo(insertionInfo);

		const params: UpdateSpaceFileRequest = {
			spaceFileList: [
				{
					spaceFileID: activePDFFile.id,
					spaceVersionID: activePDFFile.spaceVersionId,
					spaceID: activePDFFile.parent.id,
					settings: {
						insertionInfo: activePDFFile.insertionInfo,
					},
				},
			],
			portfolioID: activePDFFile.parent.portfolioId,
		};

		await this.props.spaceViewRenderer.transport.updateSpaceFiles(params);
		this.props.spaceViewRenderer.setInsertionInfo(activePDFFile.insertionInfo);

		this.forceUpdate();
	};

	private setActiveTool = (id: SpaceTool) => {
		const {spaceViewRenderer} = this.props;

		if (arrayOfSelectionToolTypes.includes(id as SelectionToolType)) {
			this.setSelectionToolType(id as SelectionToolType);
		}

		if (this.state.activeToolId !== id) {
			this.setState({
				activeToolId: id,
			});
		}

		if (id !== "selection") {
			spaceViewRenderer.spaceItemController.closeLinks();
			spaceViewRenderer.spaceItemController.deselectAll();
		}
	};

	private setSelectionToolType = (type: SelectionToolType) => {
		if (type !== this.state.selectionToolType) {
			this.setState({
				selectionToolType: type,
			});
		}
	};

	private getDockedTitle(state: ISpaceViewState = this.state): DockableTitle {
		if (state.isCatalogOpen && state.isCatalogDocked) {
			return "Catalog";
		} else if (state.isBoundaryTypesWindowOpen && state.isBoundaryTypesWindowDocked) {
			return "Boundary";
		} else if (state.isUnplottedXyiconsOpen && state.isUnplottedXyiconsDocked) {
			return "Unplotted Xyicons";
		} else {
			return null;
		}
	}

	private onAddCatalogClick = (event: React.MouseEvent) => {
		if (event.target !== this.props.spaceViewRenderer.transport.appState.catalogEditorParentMaybe) {
			this.props.spaceViewRenderer.transport.appState.catalogEditorParentMaybe = event.currentTarget as HTMLDivElement;
		}
		// Defer state update so the main thread stays responsive; popup mounts without blocking the UI.
		React.startTransition(() => {
			this.setState({
				isCreatePanelOpen: true,
				editedCatalog: null,
			});
		});
	};

	private onDuplicateCatalogClick = async (catalog: Catalog) => {
		const iconConfig = await this.props.spaceViewRenderer.transport.getIconConfigOfCatalog(catalog);

		this.setState({
			isCreatePanelOpen: true,
			editedCatalog: {
				catalog: catalog,
				config: iconConfig,
			},
		});
	};

	private closeCreatePanel = () => {
		this.setState({
			isCreatePanelOpen: false,
			editedCatalog: null,
		});
	};

	private forceUpdateArrow = () => {
		this.forceUpdate();
	};

	private get selectedView() {
		return this._spaceViewRenderer.actions.getSelectedView(XyiconFeature.SpaceEditor);
	}

	private stringifyFilters = () => {
		return JSON.stringify(this.selectedView.filters);
	};

	private onKeyboardPress = (event: KeyboardEvent) => {
		const {overlayedDetailedItems} = this.props.spaceViewRenderer.transport.appState.app;

		if (event.key === KeyboardListener.KEY_ESCAPE && overlayedDetailedItems?.length === 0) {
			this.setState({
				focusedItems: [],
				openedSpaceEditorPanelType: null,
			});
		}
	};

	private startListening() {
		this.listenFilters();

		KeyboardListener.getInstance().signals.down.add(this.onKeyboardPress);
	}

	private listenFilters() {
		this._disposerForFilterListener?.();
		this._disposerForFilterListener = reaction(this.stringifyFilters, () => {
			this.updateSpaceEditorFilterState(this.selectedView.filters);
		});
	}

	private stopListening() {
		this._disposerForViewChangeListener?.();
		this._disposerForViewChangeListener = null;

		this._disposerForFilterListener?.();
		this._disposerForFilterListener = null;

		KeyboardListener.getInstance().signals.down.remove(this.onKeyboardPress);
	}

	private saveStateToLocalStorage() {
		const organizationId = this.props.spaceViewRenderer.transport.appState.organizationIdByAuthData;

		if (organizationId) {
			const stateToSave = {
				isBoundaryTypesWindowDocked: this.state.isBoundaryTypesWindowDocked,
				isBoundaryTypesWindowOpen: this.state.isBoundaryTypesWindowOpen && this.state.isBoundaryTypesWindowDocked,
				isCatalogDocked: this.state.isCatalogDocked,
				isCatalogOpen: this.state.isCatalogOpen && this.state.isCatalogDocked,
				isUnplottedXyiconsDocked: this.state.isUnplottedXyiconsDocked,
				isUnplottedXyiconsOpen: this.state.isUnplottedXyiconsOpen && this.state.isUnplottedXyiconsDocked,
			};

			this.props.spaceViewRenderer.transport.services.localStorage.set(this.getLocalStorageKey(organizationId), stateToSave);
		}
	}

	private getLocalStorageKey(organizationId: string) {
		return `srv4-org-${organizationId}-spaceeditor-spaceview-dockables-open-state`;
	}

	private loadStateFromLocalStorage() {
		const organizationId = this.props.spaceViewRenderer.transport.appState.organizationIdByAuthData;

		if (organizationId) {
			const savedDockedState = this.props.spaceViewRenderer.transport.services.localStorage.get(this.getLocalStorageKey(organizationId));

			if (savedDockedState) {
				this.setState({
					...savedDockedState,
				});
			}
		}
	}

	private updateSpaceEditorFilterState = (
		filters: IFilterState = this.selectedView.filters,
		updateCaptions: boolean = true,
		addFullscreenLoaderText: boolean = true,
	) => {
		cancelAnimationFrame(this._requestAnimationFrameId);

		const ret = new Promise<void>((resolve) => {
			this._updateFilterStatePromiseResolveSet.add(resolve);
		});

		this._requestAnimationFrameId = window.requestAnimationFrame(() => {
			this.props.spaceViewRenderer.actions.updateSpaceEditorFilterState(filters, updateCaptions, addFullscreenLoaderText).then(() => {
				for (const resolve of [...this._updateFilterStatePromiseResolveSet]) {
					resolve();
					this._updateFilterStatePromiseResolveSet.delete(resolve);
				}
			});
		});

		return ret;
	};

	private onSpaceLoadReady = async () => {
		const {spaceViewRenderer} = this.props;
		const {appState} = spaceViewRenderer.transport;
		appState.fullscreenLoaderText = "Applying layers, filters, captions...";
		await this.updateSpaceEditorFilterState(undefined, false, false);
		await spaceViewRenderer.actions.updateSpaceEditorCaptions(XyiconFeature.Boundary);
		appState.fullscreenLoaderText = "";
		this.startListening();
		this.forceUpdate();
	};

	public override async componentDidMount() {
		ensureSpaceEditorDockMainSeededFromLegacyCatalogKey();
		this.loadStateFromLocalStorage();

		const {actions} = this.props.spaceViewRenderer.transport.appState;
		const features = actions.getLoadingDependencies(XyiconFeature.SpaceEditor);

		for (const feature of features) {
			await this.props.spaceViewRenderer.transport.services.feature.refreshList(feature);
		}
		// Add potential new types
		actions.getSelectedView(XyiconFeature.SpaceEditor).updateSpaceEditorViewSettings();
		this.props.spaceViewRenderer.signals.spaceLoadReady.add(this.onSpaceLoadReady);
		const {xyiconManager, boundaryManager, markupManager} = this.props.spaceViewRenderer;

		xyiconManager.signals.itemsAdd.add(this.forceUpdateArrow);
		boundaryManager.signals.itemsAdd.add(this.forceUpdateArrow);
		markupManager.signals.itemsAdd.add(this.forceUpdateArrow);

		xyiconManager.signals.itemsRemove.add(this.forceUpdateArrow);
		boundaryManager.signals.itemsRemove.add(this.forceUpdateArrow);
		markupManager.signals.itemsRemove.add(this.forceUpdateArrow);
	}

	public override componentDidUpdate(prevProps: Readonly<IBasicPropsFor3DTweaking>, prevState: Readonly<ISpaceViewState>, snapshot?: any): void {
		const {app} = this.props.spaceViewRenderer.transport.appState;
		// Adjust docked state / sidegrid, because they're conflicting with each other, only one of them can be rendered at a time on a side
		if (app.graphicalTools.gridItemSide.includes("left")) {
			const prevDockedTitle = this.getDockedTitle(prevState);
			const currentDockedTitle = this.getDockedTitle(this.state);

			if (prevDockedTitle !== currentDockedTitle && !!currentDockedTitle) {
				app.graphicalTools.gridItemSide = "";
			} else {
				const wasCatalogDockedAndOpen =
					prevState.isCatalogDocked && this.state.isCatalogDocked && prevState.isCatalogOpen && this.state.isCatalogOpen;

				if (wasCatalogDockedAndOpen) {
					this.setState({
						isCatalogDocked: false,
						isCatalogOpen: false,
					});
				}

				const wasBoundaryTypesWindowDockedAndOpen =
					prevState.isBoundaryTypesWindowDocked &&
					this.state.isBoundaryTypesWindowDocked &&
					prevState.isBoundaryTypesWindowOpen &&
					this.state.isBoundaryTypesWindowOpen;

				if (wasBoundaryTypesWindowDockedAndOpen) {
					this.setState({
						isBoundaryTypesWindowDocked: false,
						isBoundaryTypesWindowOpen: false,
					});
				}

				const wasUnplottedXyiconsWindowDockedAndOpen =
					prevState.isUnplottedXyiconsDocked &&
					this.state.isUnplottedXyiconsDocked &&
					prevState.isUnplottedXyiconsOpen &&
					this.state.isUnplottedXyiconsOpen;

				if (wasUnplottedXyiconsWindowDockedAndOpen) {
					this.setState({
						isUnplottedXyiconsDocked: false,
						isUnplottedXyiconsOpen: false,
					});
				}
			}
		}

		if (prevProps.spaceViewRenderer !== this.props.spaceViewRenderer) {
			this._spaceViewRenderer = this.props.spaceViewRenderer;
		}

		const layoutStateChanged =
			prevState.isCatalogDocked !== this.state.isCatalogDocked ||
			prevState.isCatalogOpen !== this.state.isCatalogOpen ||
			prevState.isBoundaryTypesWindowDocked !== this.state.isBoundaryTypesWindowDocked ||
			prevState.isBoundaryTypesWindowOpen !== this.state.isBoundaryTypesWindowOpen ||
			prevState.isUnplottedXyiconsDocked !== this.state.isUnplottedXyiconsDocked ||
			prevState.isUnplottedXyiconsOpen !== this.state.isUnplottedXyiconsOpen;

		if (layoutStateChanged) {
			this.triggerSpaceResize();
		}

		this.saveStateToLocalStorage();
	}

	public override componentWillUnmount() {
		this.stopListening();

		const {xyiconManager, boundaryManager, markupManager} = this.props.spaceViewRenderer;

		xyiconManager.signals.itemsAdd.remove(this.forceUpdateArrow);
		boundaryManager.signals.itemsAdd.remove(this.forceUpdateArrow);
		markupManager.signals.itemsAdd.remove(this.forceUpdateArrow);

		xyiconManager.signals.itemsRemove.remove(this.forceUpdateArrow);
		boundaryManager.signals.itemsRemove.remove(this.forceUpdateArrow);
		markupManager.signals.itemsRemove.remove(this.forceUpdateArrow);

		this.props.spaceViewRenderer.signals.spaceLoadReady.remove(this.onSpaceLoadReady);
	}

	private gridItemSide = (filterBySide: GridSide) => {
		const {gridItemSide} = this.props.spaceViewRenderer.transport.appState.app.graphicalTools;

		if (!gridItemSide) {
			return gridItemSide;
		}

		const splitted = gridItemSide.split("-");

		const side: GridSide = splitted[0] as GridSide;

		if (side !== filterBySide) {
			return "";
		}

		const item: GridItem = splitted[1] as GridItem;

		return (
			<SideGrid
				side={side}
				itemFeature={item === "xyicon" ? XyiconFeature.Xyicon : XyiconFeature.Boundary}
				focusedItems={this.state.focusedItems}
				registerClosePopupsHandler={this.registerActionBarCloseHandler}
			/>
		);
	};

	private toggleDetailsPanel = () => {
		if (this.state.focusedItems.length > 0) {
			this.onCloseDetailsPanel();
		} else {
			const selectedItems = this.props.spaceViewRenderer.spaceItemController.selectedItems;
			this.onFocusItems(selectedItems.map((spaceItem) => spaceItem.modelData as ISpaceItemModel));
		}
	};

	private onCloseDetailsPanel = () => {
		this.setState((state): Pick<ISpaceViewState, "focusedItems"> | null => (state.focusedItems.length ? {focusedItems: []} : null));
	};

	private onSelectItems = (spaceItems: ISpaceItemModel[]) => {
		const {spaceItemController} = this.props.spaceViewRenderer;
		const spaceItemIds = ArrayUtils.removeDuplicates(spaceItems.map((item) => item.id))
			.toSorted(StringUtils.sortIgnoreCase)
			.join();

		const currentSelectedItems = spaceItemController.selectedItems;
		const currentSelectedSpaceItemIds = ArrayUtils.removeDuplicates(currentSelectedItems.map((item) => item.id))
			.toSorted(StringUtils.sortIgnoreCase)
			.join();

		if (currentSelectedSpaceItemIds !== spaceItemIds) {
			spaceItemController.deselectAll(false, false, false);

			for (const spaceItem of spaceItems) {
				const finalSpaceItem = (spaceItem as BoundarySpaceMap).isBoundarySpaceMap ? (spaceItem as BoundarySpaceMap).parent : spaceItem;
				const itemManager = this.props.spaceViewRenderer.getItemManager(finalSpaceItem.ownFeature);

				if (itemManager) {
					const spaceItem3DMaybe = itemManager.getItemById(finalSpaceItem.id);
					if (spaceItem3DMaybe) {
						spaceItem3DMaybe.select();
					}
				}
			}

			for (const feature of [XyiconFeature.Xyicon, XyiconFeature.Boundary, XyiconFeature.Markup]) {
				const itemManager = this.props.spaceViewRenderer.getItemManager(feature);
				itemManager?.updateSelectionBox();
			}

			spaceItemController.updateBoundingBox();
			spaceItemController.updateActionBar(false, false);
		}
	};

	private onFocusItems = (spaceItems: ISpaceItemModel[]) => {
		const finalSpaceItems = ArrayUtils.removeDuplicates(
			spaceItems.map((item: ISpaceItemModel) =>
				(item as BoundarySpaceMap)?.isBoundarySpaceMap ? (item as BoundarySpaceMap).parent : item,
			) as ISpaceItemModel[],
		);
		// Deselecting already unfocused canvas items should not rerender both grids.
		this.setState((state) => (ArrayUtils.equals(state.focusedItems, finalSpaceItems) ? null : {focusedItems: finalSpaceItems}));
	};

	private getFocusedItems = () => {
		return this.state.focusedItems;
	};

	private resetOpenedSpaceEditorPanelState = () => {
		this.setState((state): Pick<ISpaceViewState, "openedSpaceEditorPanelType"> | null =>
			state.openedSpaceEditorPanelType === null ? null : {openedSpaceEditorPanelType: null},
		);
	};

	private registerActionBarCloseHandler = (closeHandler: () => void) => {
		this._closeActionBarPopups = closeHandler;
	};

	private onOpenPanelClick = (type: TSpaceEditorPanelTypes) => {
		this._closeActionBarPopups?.();
		this.setState({openedSpaceEditorPanelType: type});
	};

	public override render() {
		const {appState} = this.props.spaceViewRenderer.transport;
		const space = appState.space;
		const dockedTitle = this.getDockedTitle();
		const isDockedAndOpen = !!dockedTitle;
		const {graphicalTools} = appState.app;
		const {photoSphereSceneManager} = graphicalTools;
		const isInMixedOrSplitViewMode = ["mixed", "split", "transitioning"].includes(photoSphereSceneManager.viewMode);

		const isLeftGridOpen = !isInMixedOrSplitViewMode && this.gridItemSide("left");
		const isRightGridOpen = !isInMixedOrSplitViewMode && this.gridItemSide("right");
		const isSideGridOpen = Boolean(isLeftGridOpen || isRightGridOpen);
		const hasDockedPanel = !isInMixedOrSplitViewMode && isDockedAndOpen && !this.props.spaceViewRenderer.isMeasureToolBarOpen;
		/** Docked + main, no side grids: one key for catalog, boundary, unplotted — matches xyicon list when catalog is docked. */
		const dockedFirstPaneWithMainNoSideGrids = hasDockedPanel && !isSideGridOpen;
		const spaceEditorSplitterKey = dockedFirstPaneWithMainNoSideGrids
			? SPLITTER_V5_LOCAL_STORAGE_KEY_SPACE_EDITOR_DOCK_MAIN
			: "v5-splitter-space-editor-main";

		if (space) {
			return (
				<SpaceEditorViewStyled>
					<SplitterV5 localStorageKey={spaceEditorSplitterKey}>
						{hasDockedPanel && (
							<DockableV5
								spaceViewRenderer={this.props.spaceViewRenderer}
								setDocked={this.setDockableDocked}
								setOpen={this.setDockableOpen}
								isDocked={isDockedAndOpen}
								title={dockedTitle}
								setActiveTool={this.setActiveTool}
								onAddCatalogClick={this.onAddCatalogClick}
								onDuplicateCatalogClick={this.onDuplicateCatalogClick}
								onCreateUnplottedXyicons={Functions.emptyFunction}
							/>
						)}
						{isLeftGridOpen}
						<SpaceEditorV5
							spaceViewRenderer={this.props.spaceViewRenderer}
							setDockableDocked={this.setDockableDocked}
							setDockableOpen={this.setDockableOpen}
							isCatalogOpen={this.state.isCatalogOpen}
							isBoundaryTypesWindowOpen={this.state.isBoundaryTypesWindowOpen}
							isUnplottedXyiconsOpen={this.state.isUnplottedXyiconsOpen}
							isCatalogDocked={this.state.isCatalogDocked}
							isBoundaryTypesWindowDocked={this.state.isBoundaryTypesWindowDocked}
							isUnplottedXyiconsDocked={this.state.isUnplottedXyiconsDocked}
							setScale={this.setScale}
							confirmAlignment={this.confirmAlignment}
							thumbnail={space.thumbnailFileURL}
							mode={SpaceEditorMode.NORMAL}
							activeToolId={this.state.activeToolId}
							setActiveTool={this.setActiveTool}
							selectionToolType={this.state.selectionToolType}
							selectItems={this.onSelectItems}
							focusItems={this.onFocusItems}
							getFocusedItems={this.getFocusedItems}
							onAddCatalogClick={this.onAddCatalogClick}
							onDuplicateCatalogClick={this.onDuplicateCatalogClick}
							toggleDetailsPanel={this.toggleDetailsPanel}
							onOpenPanelClick={this.onOpenPanelClick}
							onCloseActivePanel={this.resetOpenedSpaceEditorPanelState}
							activePanel={this.state.openedSpaceEditorPanelType}
							isDetailsPanelOpen={this.state.focusedItems.length > 0}
							forceUpdateDetailsPanel={this.forceUpdateArrow}
							isSideGridOpen={isSideGridOpen}
							closeSideGridPopups={this._closeActionBarPopups}
						/>
						{isRightGridOpen}
					</SplitterV5>
					{!isInMixedOrSplitViewMode && (
						<>
							<CatalogCreateV5
								editedCatalogMaybe={this.state.editedCatalog}
								isOpen={this.state.isCreatePanelOpen}
								onClose={this.closeCreatePanel}
								style={{transform: "translateX(-35px)"}}
							/>
							<Panel
								isOpen={this.state.focusedItems.length > 0}
								panelId="space-editor-details-panel"
							>
								<DetailsContentV5
									items={this.state.focusedItems}
									feature={this.state.focusedItems[0]?.ownFeature}
									features={[XyiconFeature.Xyicon, XyiconFeature.Boundary]}
									isPortTemplateEditorOpen={false}
									setPortTemplateEditorOpen={Functions.emptyFunction}
									onCloseOverlayedDetaislPanel={this.onCloseDetailsPanel}
									onSelect={this.onSelectItems}
									onFocus={this.onFocusItems}
									noInitials={XyiconFeature.Link === this.state.focusedItems[0]?.ownFeature}
								/>
							</Panel>
							<SpaceEditorPanelV5
								openedSpaceEditorPanelType={this.state.openedSpaceEditorPanelType}
								onClose={this.resetOpenedSpaceEditorPanelState}
							/>
						</>
					)}
				</SpaceEditorViewStyled>
			);
		}

		// TODO loading?
		return null;
	}
}

const SpaceEditorViewStyled = styled.div`
	${VerticalFlexStyle}
	flex: 1;
	min-height: 0;
	position: relative;
`;
